import { Box, Link, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";

const AboutBox = styled(Box)(
    ({ theme }) => ({
        padding: "6.4rem 0",
        display: "flex",
        flexDirection: "column",
        [theme.breakpoints.up("sm")]: {
            paddingTop: "7rem",
        },
    })
);

const AboutSection = styled((props) => (
    <Box variant="section" {...props} />
))(({ theme }) => ({
    margin: theme.spacing(4, "4vw", 0, "4vw"),
}));

const AboutSectionTitle = styled(Typography)(
    ({ theme }) => ({
        fontFamily: "montserrat",
        fontWeight: 800,
        fontSize: "2.4rem",
        color: "purple",
    })
);

const AboutSectionSubtitle = styled(Typography)(
    ({ theme }) => ({
        fontFamily: "montserrat",
        fontWeight: 600,
        fontSize: "2rem",
        margin: theme.spacing(1.5, 0, 1, 0),
    })
);

const AboutSectionText = styled((props) => (
    <Typography component="div" {...props} />
))(({ theme }) => ({
    fontFamily: "inter",
    fontWeight: 500,
    fontSize: "1.4rem",
    marginBottom: theme.spacing(0.5),
    [theme.breakpoints.up("md")]: {
        fontSize: "1.5rem",
    },
    [theme.breakpoints.up("xl")]: {
        fontSize: "1.6rem",
    },
}));

export default function About(props) {
    return (
        <AboutBox>
            <Link target="_blank" rel="noreferrer noopener" href="https://www.youtube.com/watch?v=KJSj_DCQ1bI&t=1s">
                <img style={{ width: "100%", backgroundColor: "black" }} src={`${process.env.PUBLIC_URL}/about/intro.gif`} alt="introduction gif video about LocEdge" />
            </Link>
            <AboutSection id="overview">
                <AboutSectionTitle>
                    OVERVIEW
                </AboutSectionTitle>
                <AboutSectionSubtitle>
                    Background and Motivation
                </AboutSectionSubtitle>
                <AboutSectionText>
                    Content Delivery Networks (CDNs) reduce latency by providing resources to users from the <b>nearest</b> servers.
                    Many CDN providers rely on IP Anycast to find the "best" routing paths. Yet, such routings may be <b>suboptimal</b>, leading to poor performance.
                </AboutSectionText>
                <img style={{ width: "80%", maxWidth: "64rem", margin: "1rem 0 2rem 0" }} src={`${process.env.PUBLIC_URL}/about/motivation.png`} loading="lazy" alt="background" />
                <AboutSectionText>
                    To monitor and understand CDN inefficiencies, one'll need to know the <b>physical location</b> of CDN edge servers.
                </AboutSectionText>

                <AboutSectionSubtitle>
                    Existing Solutions
                </AboutSectionSubtitle>
                <AboutSectionText>
                    <b style={{ textDecoration: "underline" }}>GeoIP databases</b> (e.g., Maxmind, IP2Location) are more accurate and reliable for locating Internet clients than servers.
                    What's more, many CDN providers use IP Anycast. So it is not a good idea to use these databases to locate CDN edge servers.
                </AboutSectionText>
                <AboutSectionText>
                    <b style={{ textDecoration: "underline" }}>Latency-based algorithms</b> are applicable to Anycast replicas and provide high accuracy.
                    However, it is costly and inconvenient to use them since you would need to conduct active measurements at numerous vantage points around the world to collect data.
                </AboutSectionText>
                <AboutSectionText>
                    <b style={{ textDecoration: "underline" }}>Leveraging geographic hints in DNS names</b> seems to be a perfect solution. Yet, we found it not widely applicable in practice,
                    as many CDN providers and network operators do not embed geographic hints in DNS names.
                </AboutSectionText>

                <AboutSectionSubtitle>
                    Our Idea
                </AboutSectionSubtitle>
                <AboutSectionText>
                    We noticed that many CDN providers would also embed <b>geographic hints (geohints)</b> in HTTP headers, e.g.,
                    Cloudflare places the IATA airport code for the city where the edge server is located at the end of the "cf-ray" response header.
                    Yet, not all CDN providers follow such naming conventions. Some vendors may use unintelligible random strings as geohints.
                    Therefore, to use them for server geolocation, we'll need to:
                    <span style={{ display: "block", marginTop: 4 }}><b>1. Identify and extract geohints from specific HTTP headers</b></span>
                    <span style={{ display: "block" }}><b>2. Find their corresponding locations</b></span>
                </AboutSectionText>
            </AboutSection>

            <AboutSection id="methodology">
                <AboutSectionTitle>
                    METHODOLOGY
                </AboutSectionTitle>
                <img style={{ width: "80%", maxWidth: "64rem", marginTop: "1rem" }} src={`${process.env.PUBLIC_URL}/about/methodology.png`} loading="lazy" alt="architecture" />
                <AboutSectionSubtitle>
                    Components
                </AboutSectionSubtitle>
                <AboutSectionText>
                    <b style={{ textDecoration: "underline" }}>Monitor</b>:
                    Collects HTTP responses and latencies with 229 probes (<Link target="_blank" rel="noreferrer noopener" href="https://www.uptrends.com/checkpoints">Click</Link> to see their locations).
                </AboutSectionText>
                <AboutSectionText>
                    <b style={{ textDecoration: "underline" }}>Extractor</b>:
                    Identifies and extracts geohints using TF-IDF.
                </AboutSectionText>
                <AboutSectionText>
                    <b style={{ textDecoration: "underline" }}>Filter</b>:
                    Detects Speed-of-Light violations to filter out ineligible geohints
                </AboutSectionText>
                <AboutSectionText>
                    <b style={{ textDecoration: "underline" }}>Classifier</b>:
                    Determines the corresponding location of each confirmed geohints
                </AboutSectionText>
                <AboutSectionSubtitle>
                    Results
                </AboutSectionSubtitle>
                <AboutSectionText>
                    A <b style={{ textDecoration: "underline" }}>ruleset</b> for extracting geohints from specific headers
                </AboutSectionText>
                <AboutSectionText>
                    A <b style={{ textDecoration: "underline" }}>dataset</b> of the mappings between each geohint and a physical location (city).
                </AboutSectionText>
            </AboutSection>

            <AboutSection id="eval">
                <AboutSectionTitle>
                    EVALUATION
                </AboutSectionTitle>
                <AboutSectionSubtitle>
                    Metric
                </AboutSectionSubtitle>
                <AboutSectionText>
                    The <b>average error distance</b> between the predicted city and the actual location of each endpoint
                </AboutSectionText>
                <AboutSectionSubtitle>
                    Ground Truth
                </AboutSectionSubtitle>
                <AboutSectionText>
                    The official Geohint-to-City mappings from Amazon CloudFront, CacheFly, Fastly, KeyCDN, StackPath, and Tata communications.
                </AboutSectionText>
                <AboutSectionSubtitle>
                    Baseline
                </AboutSectionSubtitle>
                <AboutSectionText>
                    For every endpoint, we evaluate our approach against the best among the results of Hoiho, Maxmind, and IP2Location.
                    That is, we use a mixture of the minimum error distance of these three geolocation databases for each endpoint (IP address)
                    as the baseline. This is an ideal scenario and our approach still significantly outperforms the baseline:
                </AboutSectionText>
                <img style={{ width: "80%", maxWidth: "64rem", marginTop: "2rem" }} src={`${process.env.PUBLIC_URL}/about/evaluation.png`} loading="lazy" alt="result table" />
            </AboutSection>

            <AboutSection id="cite">
                <AboutSectionTitle>
                    CITATION
                </AboutSectionTitle>
                <AboutSectionSubtitle>
                    If you use our datasets or the <Link href="https://www.npmjs.com/package/locedge">LocEdge</Link> tool, you are required to cite:
                </AboutSectionSubtitle>
                <AboutSectionText>
                    Run Huang, Mengying Zhou, Tiancheng Guo, Yang Chen.
                    Locating CDN Edge Servers with HTTP Responses.
                    In Proc. of ACM SIGCOMM '22 Demos and Posters.
                </AboutSectionText>
            </AboutSection>
        </AboutBox>
    );
};